import { ClipboardEvent, useCallback, useMemo, useState } from 'react'
import { ErrorCode, FileError, FileRejection, useDropzone } from 'react-dropzone'
import {
  ATTACHMENT_MAX_BYTES,
  VIDEO_ATTACHMENT_ACCEPT,
  VIDEO_ATTACHMENT_MAX_BYTES,
} from '@common/constants/client'
import { isVideoAttachment } from '@common/helpers/attachments'
import { humanReadableFileSize } from '@common/helpers/helpers'
import moment from 'moment'

/**
 * The attachment pipeline shared by every composer in the portal: dropzone,
 * paste-to-upload and per-file independent uploads.
 *
 * Extracted from the Atlas chat input when team messaging needed the same
 * behaviour. All of it is subtle enough — the per-file isolation that keeps one
 * failure from killing a batch, the screenshot renaming — that a second copy
 * would drift.
 *
 * Callers differ only in where a file goes, which is the `upload` callback, and
 * in any once-per-batch setup that upload needs, which is `prepareBatch`.
 */

export enum AttachmentUploadStatus {
  Uploading = 'uploading',
  Ready = 'ready',
  Failed = 'failed',
}

/** The minimum a caller's upload response must carry to be usable. */
/**
 * Whatever the upload endpoint hands back for a staged file.
 *
 * Deliberately unconstrained: this hook carries the metadata from the upload
 * response through to the caller's send handler and never reads a field of it, so
 * naming one here only forced every surface to speak the same storage's language.
 * Atlas chat uploads return a Box file id; channel messages return an S3 key.
 */
export type IUploadedFileMeta = object

export interface IPendingAttachment<TMeta extends IUploadedFileMeta = IUploadedFileMeta> {
  // Stable id for keying + remove. Generated client-side via crypto.randomUUID.
  pendingId: string
  file: File
  status: AttachmentUploadStatus
  error?: string
  // Populated once the upload finishes successfully.
  meta?: TMeta
}

// Note: legacy binary Office formats (.doc, .ppt) are intentionally absent —
// the agent's code_execution sandbox reads docx/pptx with python-docx /
// python-pptx, neither of which supports the older formats. Users get a
// clear "save as .docx" prompt from the dropzone instead of an opaque
// agent failure mid-run. See server/src/services/atlasai/helpers/fileInjection.ts.
export const SUPPORTED_ATTACHMENT_ACCEPT: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'image/bmp': ['.bmp'],
  // iPhone photos. The server stores them as JPEG, so they render in every browser.
  'image/heic': ['.heic'],
  'image/heif': ['.heif'],
  'text/plain': ['.txt', '.log'],
  'text/markdown': ['.md'],
  'text/csv': ['.csv'],
  'application/json': ['.json'],
  'text/html': ['.html'],
  'application/xml': ['.xml'],
  // Stored and downloaded as-is; the second type is what Windows reports for a .zip.
  'application/zip': ['.zip'],
  'application/x-zip-compressed': ['.zip'],
}

const ACCEPT_WITH_VIDEO: Record<string, string[]> = {
  ...SUPPORTED_ATTACHMENT_ACCEPT,
  ...VIDEO_ATTACHMENT_ACCEPT,
}

const VIDEO_EXTENSIONS = Object.values(VIDEO_ATTACHMENT_ACCEPT).flat()

const REJECTION_MESSAGE_TIMEOUT_MS = 6000

/**
 * The metadata an upload action resolved with. The promise middleware unwraps the API
 * response into `action.data`; some routes wrap it again as `{ data: { data } }`.
 */
export const readUploadResponse = <TMeta extends IUploadedFileMeta>(
  response: unknown,
): TMeta | undefined => {
  const wrapper = response as { data?: { data?: TMeta } & TMeta } | undefined
  return wrapper?.data?.data ?? wrapper?.data ?? undefined
}

interface IParams<TContext> {
  /**
   * Upload one file. Called once per file, in parallel, with whatever
   * `prepareBatch` resolved for this batch.
   */
  upload: (file: File, context: TContext | undefined) => Promise<unknown>
  /**
   * Once-per-batch setup, awaited before the batch's uploads start — e.g.
   * lazily creating the record the files should nest under. A rejection is
   * swallowed and the uploads proceed with `undefined`.
   */
  prepareBatch?: () => Promise<TContext | undefined>
  /** Blocks drops, the picker and paste while true. */
  isDisabled?: boolean
  /**
   * Detach the drag handlers entirely. For touch surfaces: there is no file to
   * drag in from, and the handlers are not merely useless there — iOS can begin a
   * native drag from a long press, which fires `dragenter` on this root and leaves
   * `isDragActive` stuck true with no matching `dragleave`. Callers paint that
   * state (the composer borders itself in `--border-focus`), so the result is a
   * field that looks focused when nothing is.
   */
  noDrag?: boolean
  /** Accept mp4/webm/mov under the larger video cap. Only Team Messaging's server takes them. */
  acceptVideo?: boolean
}

const generatePendingId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`

// By extension as well as mime: some systems hand over a `.mov` with an empty `type`.
const isVideoFile = (file: File): boolean =>
  isVideoAttachment(file.type) ||
  VIDEO_EXTENSIONS.some((extension) => file.name.toLowerCase().endsWith(extension))

const maxBytesFor = (file: File): number =>
  isVideoFile(file) ? VIDEO_ATTACHMENT_MAX_BYTES : ATTACHMENT_MAX_BYTES

// Per file rather than dropzone's single `maxSize`, so a video gets its own cap. A video
// the caller doesn't accept is rejected on type first, so its cap never matters.
const validateFileSize = (file: File): FileError | null => {
  const maxBytes = maxBytesFor(file)
  if (file.size <= maxBytes) return null
  return { code: ErrorCode.FileTooLarge, message: `File is larger than ${maxBytes} bytes` }
}

const summarizeRejection = (rej: FileRejection): string => {
  const reason = rej.errors[0]
  if (!reason) return rej.file.name
  if (reason.code === 'file-too-large') {
    const maxSize = humanReadableFileSize(maxBytesFor(rej.file)).join(' ')
    return `${rej.file.name} is too large (max ${maxSize})`
  }
  if (reason.code === 'file-invalid-type') return `${rej.file.name} isn't a supported type`
  return `${rej.file.name}: ${reason.message}`
}

const useFileAttachments = <TMeta extends IUploadedFileMeta, TContext = void>({
  upload,
  prepareBatch,
  isDisabled = false,
  noDrag = false,
  acceptVideo = false,
}: IParams<TContext>) => {
  const [attachments, setAttachments] = useState<IPendingAttachment<TMeta>[]>([])
  const [rejectionMessage, setRejectionMessage] = useState<string | null>(null)

  const reset = useCallback(() => {
    setAttachments([])
  }, [])

  /**
   * Replace the staged list with files that are already uploaded — a draft being
   * restored, where the upload survived but the bytes behind it are long gone.
   *
   * Entries arrive fully formed rather than as bare metadata because this hook is
   * deliberately ignorant of what `TMeta` holds (see `IUploadedFileMeta`): only
   * the caller knows which of its fields names the file. It hands over a `File`
   * to stand behind the chip and the metadata to send, and gets back entries
   * indistinguishable from ones that were just uploaded.
   *
   * Replaces rather than appends, so restoring a draft is also what clears the
   * previous one's chips — a list that accumulated across drafts would attach the
   * wrong files to the next message.
   */
  const restore = useCallback((entries: Array<{ file: File; meta: TMeta }>) => {
    setAttachments(
      entries.map(({ file, meta }) => ({
        pendingId: generatePendingId(),
        file,
        status: AttachmentUploadStatus.Ready,
        meta,
      })),
    )
  }, [])

  const stageFiles = useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) return
      const newPending: IPendingAttachment<TMeta>[] = acceptedFiles.map((file) => ({
        pendingId: generatePendingId(),
        file,
        status: AttachmentUploadStatus.Uploading,
      }))
      setAttachments((prev) => [...prev, ...newPending])

      void (async () => {
        // Resolved once for the whole batch so every file in this drop lands in
        // the same place. On failure the uploads still go ahead without it.
        let context: TContext | undefined
        try {
          context = await prepareBatch?.()
        } catch {
          context = undefined
        }

        // Fire each upload independently so one failure doesn't block the rest.
        newPending.forEach(async (entry) => {
          try {
            const data = readUploadResponse<TMeta>(await upload(entry.file, context))
            // Only "did we get metadata back at all" — which field identifies the
            // stored file is the caller's business (a Box id, an S3 key), and
            // checking for one here is what tied this hook to Box.
            if (!data || Object.keys(data).length === 0) {
              throw new Error('Upload returned no file metadata')
            }
            setAttachments((prev) =>
              prev.map((a) =>
                a.pendingId === entry.pendingId
                  ? { ...a, status: AttachmentUploadStatus.Ready, meta: data }
                  : a,
              ),
            )
          } catch (err) {
            const errMsg = err instanceof Error ? err.message : 'Upload failed'
            setAttachments((prev) =>
              prev.map((a) =>
                a.pendingId === entry.pendingId
                  ? { ...a, status: AttachmentUploadStatus.Failed, error: errMsg }
                  : a,
              ),
            )
          }
        })
      })()
    },
    [upload, prepareBatch],
  )

  /** The dropzone's way in — a drop, or a file chosen through the picker. */
  const handleDropFiles = useCallback(
    (acceptedFiles: File[]) => {
      stageFiles(acceptedFiles)
    },
    [stageFiles],
  )

  const handleRemoveAttachment = useCallback((pendingId: string) => {
    setAttachments((prev) => prev.filter((a) => a.pendingId !== pendingId))
  }, [])

  // react-dropzone silently discards rejected files unless we hand it a
  // handler. Surface a transient inline message so the user sees what was
  // rejected and why (size cap vs unsupported type vs too many files).
  const handleDropRejected = useCallback((rejections: FileRejection[]) => {
    if (rejections.length === 0) return
    const summary = rejections.slice(0, 3).map(summarizeRejection).join(' · ')
    const more = rejections.length > 3 ? ` (+${rejections.length - 3} more)` : ''
    setRejectionMessage(`${summary}${more}`)
    window.setTimeout(() => setRejectionMessage(null), REJECTION_MESSAGE_TIMEOUT_MS)
  }, [])

  const {
    getRootProps,
    getInputProps,
    open: openFilePicker,
    isDragActive,
  } = useDropzone({
    onDrop: handleDropFiles,
    onDropRejected: handleDropRejected,
    accept: acceptVideo ? ACCEPT_WITH_VIDEO : SUPPORTED_ATTACHMENT_ACCEPT,
    validator: validateFileSize,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    noDrag,
    // react-dropzone binds its own `onPaste` to the root from v20 on, which stages a
    // pasted screenshot a second time — raw, skipping `handlePaste`'s rename, since
    // that handler sits on the editor *inside* this root and only calls
    // `preventDefault`. `handlePaste` below is this hook's paste support.
    noPaste: true,
    disabled: isDisabled,
  })

  // Paste handler: pulls files out of clipboard items (handles both file
  // copies and screenshot/image pastes from the OS) and feeds them into the
  // same upload pipeline as drag-and-drop. Text pastes are untouched — when
  // no files are present we let Draft.js handle the event normally.
  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLDivElement>) => {
      if (isDisabled) return
      const items = event.clipboardData?.items
      if (!items || items.length === 0) return

      const files: File[] = []
      for (const item of Array.from(items)) {
        if (item.kind !== 'file') continue
        const file = item.getAsFile()
        if (!file) continue
        // Screenshot pastes often arrive as `image.png` with no mtime — give
        // them a slightly more useful default name with a timestamp so the
        // chip + Box upload aren't ambiguous when the user pastes multiple
        // in a row.
        if (file.name === 'image.png' || /^image\.(png|jpg|jpeg|gif|webp|bmp)$/i.test(file.name)) {
          const ts = moment().format('YYYY-MM-DD_HH-mm-ss')
          const ext = file.name.split('.').pop() || 'png'
          const renamed = new File([file], `pasted-${ts}.${ext}`, { type: file.type })
          files.push(renamed)
        } else {
          files.push(file)
        }
      }

      if (files.length > 0) {
        event.preventDefault()
        stageFiles(files)
      }
    },
    [isDisabled, stageFiles],
  )

  const isUploading = useMemo(
    () => attachments.some((a) => a.status === AttachmentUploadStatus.Uploading),
    [attachments],
  )

  const getReadyAttachments = useCallback(
    (): TMeta[] =>
      attachments
        .filter(
          (a): a is IPendingAttachment<TMeta> & { meta: TMeta } =>
            a.status === AttachmentUploadStatus.Ready && !!a.meta,
        )
        .map((a) => a.meta),
    [attachments],
  )

  return {
    attachments,
    rejectionMessage,
    isUploading,
    handleRemoveAttachment,
    handlePaste,
    reset,
    restore,
    getReadyAttachments,
    dropzone: { getRootProps, getInputProps, openFilePicker, isDragActive },
  }
}

export default useFileAttachments
